import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';

import { StackedDialog, useSelectMenuProps } from '../../../utilities/ModalStackContext';
import { StateManager } from '../../../utilities/stateManager';
import { DEFAULT_CONCIERGE_FEE, applyIncentive } from '../../../utilities/financeInvoice';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

// A lender incentive hits two places at once: it raises the finance invoice and
// it lands as real money in the bank. This does both from one form so neither
// half can be forgotten.
export default function AddIncentiveModal({ open, onClose, stockNumber, invoice, defaultAccount = '', onApplied }) {
  const selectMenuProps = useSelectMenuProps();
  const [memo, setMemo] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [account, setAccount] = React.useState(defaultAccount);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setMemo('');
    setAmount('');
    setAccount(defaultAccount);
  }, [open, defaultAccount]);

  const handleSave = async () => {
    if (!memo.trim()) {
      StateManager.setAlertAndOpen('A memo is required', 'warning');
      return;
    }
    const value = parseFloat(amount);
    if (!value) {
      StateManager.setAlertAndOpen('Enter an amount', 'warning');
      return;
    }
    if (!account) {
      StateManager.setAlertAndOpen('Pick the receiving bank', 'warning');
      return;
    }
    setSaving(true);
    try {
      const result = await applyIncentive({
        stockNumber,
        memo,
        amount: value,
        account,
        existingInvoice: invoice || null,
      });
      StateManager.setAlertAndOpen(
        result.createdInvoice
          ? `Incentive added — created a finance invoice with the ${USD(DEFAULT_CONCIERGE_FEE)} concierge fee`
          : 'Incentive added to the invoice and deposited',
        'success',
      );
      onApplied?.(result);
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to add the incentive', 'error');
    }
    setSaving(false);
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Add Lender Incentive</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{stockNumber}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Memo"
            size="small"
            fullWidth
            autoFocus
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="e.g. Woodside reserve"
          />
          <TextField
            label="Amount"
            type="number"
            size="small"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Receiving Bank</InputLabel>
            <Select value={account} onChange={(e) => setAccount(e.target.value)} label="Receiving Bank" MenuProps={selectMenuProps}>
              {StateManager.deposit_banks.map(bank => (
                <MenuItem key={bank.value} value={bank.value}>{bank.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {!invoice && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              This car has no finance invoice yet — one will be created with the{' '}
              {USD(DEFAULT_CONCIERGE_FEE)} concierge fee.
            </Alert>
          )}

          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Raises the finance invoice and records a matching deposit.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Add Incentive'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
